import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCourse, getModules, createEnrollment, getArticlesByCourse } from '../utils/api';
import SmartImage from '../components/SmartImage';
import CourseStateBadge from '../components/CourseStateBadge';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Clock, User, BookOpen, CheckCircle, DollarSign, Video, ExternalLink, Copy, FileText, Newspaper, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import EnrollForm from './EnrollForm';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_LABELS = {
  paypal: 'PayPal',
  sinpe: 'SINPE Móvil (Costa Rica)',
  nequi: 'Nequi (Colombia)',
  daviplata: 'Daviplata (Colombia)',
  mercadopago: 'Mercado Pago',
  yappy: 'Yappy (Panamá)',
  bank_transfer: 'Transferencia Bancaria',
  other: 'Otro método',
};

const DELIVERY_LABELS = {
  online: 'Online',
  zoom: 'Zoom (en vivo)',
  hybrid: 'Híbrido',
  'in-person': 'Presencial',
};

const PaymentBlock = ({ method, val }) => (
  <div className="p-4 border rounded-xl bg-white space-y-2 text-sm">
    <p className="font-semibold text-foreground">{PAYMENT_LABELS[method] || method}</p>
    {method === 'paypal' && val.link && (
      <a href={val.link} target="_blank" rel="noreferrer"
        className="flex items-center gap-1 text-blue-600 underline">
        <ExternalLink className="w-3.5 h-3.5" />{val.link}
      </a>
    )}
    {(method === 'sinpe') && (
      <div className="flex items-center gap-3">
        <span className="font-mono text-lg font-bold">{val.number}</span>
        <span className="text-muted-foreground">— {val.name}</span>
        <button onClick={() => { navigator.clipboard.writeText(val.number); toast.success('Número copiado'); }}
          className="text-muted-foreground hover:text-foreground">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    )}
    {(method === 'nequi' || method === 'daviplata' || method === 'yappy') && (
      <div className="flex items-center gap-2">
        <span className="font-mono text-lg font-bold">{val.number}</span>
        <button onClick={() => { navigator.clipboard.writeText(val.number); toast.success('Número copiado'); }}
          className="text-muted-foreground hover:text-foreground">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    )}
    {method === 'mercadopago' && val.link && (
      <a href={val.link} target="_blank" rel="noreferrer"
        className="flex items-center gap-1 text-blue-600 underline">
        <ExternalLink className="w-3.5 h-3.5" />{val.link}
      </a>
    )}
    {method === 'bank_transfer' && <p className="whitespace-pre-line text-muted-foreground">{val.details}</p>}
    {method === 'other' && (
      <>
        {val.name && <p className="font-medium">{val.name}</p>}
        {val.details && <p className="whitespace-pre-line text-muted-foreground">{val.details}</p>}
      </>
    )}
  </div>
);

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchCourseData(); }, [id]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, modulesRes, articlesRes] = await Promise.all([
        getCourse(id),
        getModules(id),
        getArticlesByCourse(id, 6).catch(() => ({ data: [] })),
      ]);
      setCourse(courseRes.data);
      setModules(modulesRes.data);
      setRelatedArticles(articlesRes.data || []);
    } catch { toast.error('Error al cargar el curso'); }
    finally { setLoading(false); }
  };

  const handleEnroll = async () => {
    if (!user) { toast.error('Debes iniciar sesión para inscribirte'); navigate('/login'); return; }
    setEnrolling(true);
    try {
      await createEnrollment({ user_id: user.id, course_id: id, status: 'active' });
      toast.success('¡Te has inscrito exitosamente!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribirse');
    } finally { setEnrolling(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Cargando...</p></div>;
  if (!course) return <div className="min-h-screen flex items-center justify-center"><p>Curso no encontrado</p></div>;

  const enabledMethods = course.payment_methods
    ? Object.entries(course.payment_methods).filter(([, v]) => v?.enabled)
    : [];

  const isFree = course.is_free !== false;

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="course-detail-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Cover image */}
        {course.image_url && (
          <SmartImage
            src={course.image_url}
            alt={course.title}
            fallbackLabel={course.title}
            className="w-full h-56 object-cover rounded-2xl mb-8 shadow"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ─── Left column ─────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="secondary">{course.status}</Badge>
                {DELIVERY_LABELS[course.delivery_mode] && course.delivery_mode !== 'online' && (
                  <Badge className="bg-blue-100 text-blue-700">{DELIVERY_LABELS[course.delivery_mode]}</Badge>
                )}
                {!isFree && course.price && (
                  <Badge className="bg-amber-100 text-amber-700 gap-1">
                    <DollarSign className="w-3 h-3" />USD {Number(course.price).toFixed(2)}
                  </Badge>
                )}
                {isFree && <Badge className="bg-green-100 text-green-700">Gratis</Badge>}
                <CourseStateBadge course={course} showActive />
              </div>
              <h1 className="text-4xl font-heading font-bold text-primary mb-3">{course.title}</h1>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                {course.instructor_name && <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{course.instructor_name}</span>}
                {course.hours && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{course.hours} horas</span>}
                <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" />{modules.length} módulos</span>
              </div>
            </div>

            <Card>
              <CardHeader><CardTitle>Descripción</CardTitle></CardHeader>
              <CardContent><p className="text-muted-foreground whitespace-pre-line">{course.description}</p></CardContent>
            </Card>

            {course.requirements && (
              <Card>
                <CardHeader><CardTitle>Requisitos</CardTitle></CardHeader>
                <CardContent><p className="text-muted-foreground whitespace-pre-line">{course.requirements}</p></CardContent>
              </Card>
            )}

            {/* Payment Methods - show if paid and has methods */}
            {!isFree && enabledMethods.length > 0 && (
              <Card data-testid="payment-methods-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" /> Métodos de Pago
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground mb-4">
                    Realiza tu pago por cualquiera de los siguientes métodos y adjunta el comprobante al solicitar tu inscripción.
                  </p>
                  {enabledMethods.map(([key, val]) => (
                    <PaymentBlock key={key} method={key} val={val} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Zoom Link (visible to enrolled students only) */}
            {course.zoom_link && user && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Video className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-blue-800">Clases por Zoom</p>
                      <a href={course.zoom_link} target="_blank" rel="noreferrer"
                        className="text-blue-600 underline text-sm flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" />Unirse a la reunión
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enrollment Form (embedded) */}
            {showForm && (
              <Card data-testid="enrollment-form-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" /> Formulario de Inscripción
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <EnrollForm courseIdProp={id} embedMode />
                </CardContent>
              </Card>
            )}

            {/* Course Content */}
            <Card>
              <CardHeader>
                <CardTitle>Contenido del Curso</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {modules.map((module, idx) => (
                    <AccordionItem key={module.id} value={module.id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <CheckCircle className="w-5 h-5 text-accent" />
                          <div>
                            <p className="font-semibold">Módulo {idx + 1}: {module.title}</p>
                            {module.description && <p className="text-sm text-muted-foreground">{module.description}</p>}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-muted-foreground pl-8">Contenido disponible después de inscribirse</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>

          {/* ─── Right column (sticky CTA) ──────────── */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="pt-6 space-y-4">
                {!isFree && course.price && (
                  <div className="text-center">
                    <span className="text-3xl font-bold text-foreground">USD ${Number(course.price).toFixed(2)}</span>
                  </div>
                )}
                {isFree && (
                  <div className="text-center">
                    <span className="text-2xl font-bold text-green-600">Gratis</span>
                  </div>
                )}

                {/* CTA button logic */}
                {course.course_state === 'finished' ? (
                  <div className="w-full p-3 rounded-lg bg-red-50 border border-red-200 text-center" data-testid="enrollment-closed-banner">
                    <p className="text-sm font-semibold text-red-700">⛔ Inscripciones cerradas</p>
                    <p className="text-xs text-red-600/80 mt-0.5">Este curso ya finalizó. Contacta al administrador si necesitas más información.</p>
                  </div>
                ) : isFree && !course.enrollment_form_enabled ? (
                  <Button className="w-full" size="lg" onClick={handleEnroll} disabled={enrolling} data-testid="enroll-btn">
                    {enrolling ? 'Inscribiendo...' : 'Inscribirse Gratis'}
                  </Button>
                ) : course.enrollment_form_enabled ? (
                  <Button className="w-full" size="lg"
                    onClick={() => setShowForm(p => !p)}
                    variant={showForm ? 'outline' : 'default'}
                    data-testid="show-enroll-form-btn">
                    {showForm ? 'Ocultar Formulario' : (isFree ? 'Solicitar Inscripción' : 'Pagar y Solicitar Inscripción')}
                  </Button>
                ) : (
                  <Button className="w-full" size="lg" onClick={handleEnroll} disabled={enrolling} data-testid="enroll-btn">
                    {enrolling ? 'Inscribiendo...' : 'Inscribirse Ahora'}
                  </Button>
                )}

                {/* Instructor WhatsApp contact */}
                {course.instructor_whatsapp && (() => {
                  const phone = course.instructor_whatsapp.replace(/[^\d+]/g, '').replace(/^\+/, '');
                  const msg = encodeURIComponent(course.instructor_whatsapp_message || `Hola, tengo interés en el curso "${course.title}". ¿Me puedes dar más información?`);
                  return (
                    <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold text-sm transition-colors"
                      data-testid="course-whatsapp-instructor">
                      <span className="inline-block w-5 h-5 rounded-full bg-white text-green-500 text-xs font-bold flex items-center justify-center">W</span>
                      Contactar al instructor
                    </a>
                  );
                })()}

                {/* Quick payment links */}
                {!isFree && enabledMethods.length > 0 && !showForm && (
                  <div className="space-y-2">
                    {enabledMethods.slice(0, 3).map(([key, val]) => (
                      (key === 'paypal' || key === 'mercadopago') && val.link ? (
                        <a key={key} href={val.link} target="_blank" rel="noreferrer">
                          <Button variant="outline" className="w-full gap-2" size="sm">
                            <ExternalLink className="w-3.5 h-3.5" />Pagar con {PAYMENT_LABELS[key]}
                          </Button>
                        </a>
                      ) : null
                    ))}
                  </div>
                )}

                <div className="text-xs text-muted-foreground text-center space-y-1">
                  <p>Acceso completo al curso</p>
                  <p>Certificado al finalizar</p>
                  {course.delivery_mode === 'zoom' && <p>Clases en vivo por Zoom</p>}
                </div>
              </CardContent>
            </Card>

            {/* Sobre el instructor */}
            {course.instructor_name && (
              <Card data-testid="instructor-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading uppercase tracking-wide text-muted-foreground">
                    Sobre el instructor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3 items-start">
                    {course.instructor_avatar ? (
                      <img
                        src={course.instructor_avatar.startsWith('/api') ? `${API_BASE}${course.instructor_avatar}` : course.instructor_avatar}
                        alt={course.instructor_name}
                        className="w-16 h-16 rounded-full object-cover ring-2 ring-accent/30 shrink-0"
                        data-testid="instructor-avatar"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center shrink-0 ring-2 ring-accent/30">
                        <User className="w-7 h-7 text-accent" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-primary">{course.instructor_name}</p>
                      <p className="text-xs text-muted-foreground">Instructor</p>
                      {course.instructor_bio && (
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{course.instructor_bio}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-16 pt-10 border-t" data-testid="related-articles-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
                  <Newspaper className="w-3.5 h-3.5" /> CONTENIDO RELACIONADO
                </div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-primary">Artículos relacionados</h2>
                <p className="text-sm text-muted-foreground mt-1">Lecturas vinculadas a este curso.</p>
              </div>
              <Link to="/articulos">
                <Button variant="outline" size="sm">Ver todos los artículos</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map(a => (
                <Link
                  key={a.id}
                  to={`/articulos/${a.slug}`}
                  data-testid={`related-article-${a.id}`}
                  className="group bg-white rounded-2xl border overflow-hidden hover:shadow-lg hover:border-accent/40 transition-all"
                >
                  {a.featured_image ? (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <SmartImage
                        src={a.featured_image}
                        alt={a.title}
                        fallbackLabel={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <Newspaper className="w-10 h-10 text-primary/30" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-base font-heading font-bold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                      {a.title}
                    </h3>
                    {a.excerpt && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{a.excerpt}</p>}
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      {a.author_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{a.author_name}</span>}
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(a.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
